import type { FullAudit, FrequencyValue } from '~/constants/auditTypes'
import type { Position } from '~/constants/positionTypes'

export type AuditImprovementNextStep =
  | 'boolean-search'
  | 'candidate-packet'
  | 'hiring-decision-matrix'
  | 'interview-preparation'
  | 'interview-stages'
  | 'job-description'

export interface AuditHiringKitImprovement {
  id: string
  finding: string
  implementedImprovement: string
  nextStep: AuditImprovementNextStep
  nextStepLabel: string
}

interface InterviewStage {
  daysToNext?: number
  duration?: number
  goal?: unknown
  interviewers?: unknown
  stageFocusArea?: unknown
}

interface PlanFacts {
  stages: InterviewStage[]
  stageCount: number
  plannedDays: number
  interviewHours: number
  goalsCount: number
  focusAreaCount: number
  ownedStageCount: number
}

const problematicFrequencies = new Set<FrequencyValue>([
  'sometimes',
  'often',
  'always',
])

function isPresent(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim().length > 0
  return value !== undefined && value !== null && value !== false
}

function getFrequency(
  audit: FullAudit,
  field: string,
): FrequencyValue | undefined {
  const data = audit.auditData as Record<string, unknown>
  const nestedChallenges = data.challenges as
    | Record<string, unknown>
    | undefined
  return (data[field] ?? nestedChallenges?.[field]) as
    | FrequencyValue
    | undefined
}

function getPlanFacts(position: Position): PlanFacts {
  // TODO: Reconcile the Hiring Kit's existing calculateMetrics() estimates with
  // these saved interview-plan totals. This comparison intentionally reports the
  // structured plan as entered while that broader timeline cleanup is tabled.
  const data = position.positionData ?? {}
  const rawStages = data.interviewPlan?.stages ?? data.interviewStages
  const stages: InterviewStage[] = Array.isArray(rawStages) ? rawStages : []
  const focusAreas = new Set<string>()

  for (const stage of stages) {
    const values = Array.isArray(stage.stageFocusArea)
      ? stage.stageFocusArea
      : [stage.stageFocusArea]
    values.filter(isPresent).forEach((value) => focusAreas.add(String(value)))
  }

  return {
    stages,
    stageCount: stages.length,
    plannedDays: stages.reduce(
      (sum, stage) =>
        sum + (typeof stage.daysToNext === 'number' ? stage.daysToNext : 0),
      0,
    ),
    interviewHours: Math.round(
      stages.reduce(
        (sum, stage) =>
          sum + (typeof stage.duration === 'number' ? stage.duration : 0),
        0,
      ) / 60,
    ),
    goalsCount: stages.filter((stage) => isPresent(stage.goal)).length,
    focusAreaCount: focusAreas.size,
    ownedStageCount: stages.filter((stage) => isPresent(stage.interviewers))
      .length,
  }
}

function formatProcessFacts(facts: PlanFacts): string {
  if (!facts.stageCount) {
    return 'The intake documents a shared hiring process for this role.'
  }

  const details = [`${facts.stageCount}-stage interview process`]
  if (facts.plannedDays > 0) details.push(`${facts.plannedDays} planned days`)
  if (facts.interviewHours > 0) {
    details.push(
      `${facts.interviewHours} interview ${facts.interviewHours === 1 ? 'hour' : 'hours'}`,
    )
  }
  return `${details.join(' · ')}.`
}

function formatEvaluationFacts(facts: PlanFacts): string {
  if (!facts.stageCount) {
    return 'Evaluation expectations are documented in the shared intake.'
  }

  const details = [`${facts.stageCount} structured stages`]
  if (facts.goalsCount > 0)
    details.push(`${facts.goalsCount} with defined goals`)
  if (facts.focusAreaCount > 0)
    details.push(`${facts.focusAreaCount} evaluation focus areas`)
  if (facts.ownedStageCount > 0)
    details.push(`${facts.ownedStageCount} with interviewer ownership`)
  return `${details.join(' · ')}.`
}

function documentedFields(
  position: Position,
  fields: Array<[string, string]>,
  fallback: string,
): string {
  const labels = fields
    .filter(([field]) => isPresent(position.positionData?.[field]))
    .map(([, label]) => label)

  if (!labels.length) return fallback
  return `${labels.join(', ')} documented for this role.`
}

export function buildAuditHiringKitImprovements(
  audit: FullAudit,
  position: Position,
  limit = 4,
): AuditHiringKitImprovement[] {
  const facts = getPlanFacts(position)

  const candidates: Array<{
    isFinding: boolean
    row: AuditHiringKitImprovement
  }> = [
    {
      isFinding: audit.auditData.isUsingPsychTest === true,
      row: {
        id: 'psychological-testing',
        finding: 'Psychological or IQ testing was identified as an evaluation risk.',
        implementedImprovement: formatEvaluationFacts(facts),
        nextStep: 'interview-preparation',
        nextStepLabel: 'Refine Interview Methods',
      },
    },
    {
      isFinding:
        audit.auditData.closedRoleWithoutHiringWithinLast12Months === true,
      row: {
        id: 'closed-role-without-hire',
        finding: 'A recent interview process ended without making a hire.',
        implementedImprovement: documentedFields(
          position,
          [
            ['keyOwnershipAreas', 'Ownership areas'],
            ['requiredSkills', 'required skills'],
            ['roleClosingMethod', 'decision method'],
          ],
          'Role requirements and hiring decisions are documented in one shared intake.',
        ),
        nextStep: 'hiring-decision-matrix',
        nextStepLabel: 'Define Decision Rights',
      },
    },
    {
      isFinding: problematicFrequencies.has(
        getFrequency(audit, 'hiringProcessFrequency') as FrequencyValue,
      ),
      row: {
        id: 'hiring-process',
        finding: 'Hiring process efficiency was a recurring challenge.',
        implementedImprovement: formatProcessFacts(facts),
        nextStep: 'interview-stages',
        nextStepLabel: 'Refine Interview Stages',
      },
    },
    {
      isFinding: problematicFrequencies.has(
        getFrequency(audit, 'screeningEvaluationFrequency') as FrequencyValue,
      ),
      row: {
        id: 'screening-evaluation',
        finding:
          'Screening and evaluation consistency was a recurring challenge.',
        implementedImprovement: formatEvaluationFacts(facts),
        nextStep: 'interview-preparation',
        nextStepLabel: 'Open Interview Preparation',
      },
    },
    {
      isFinding: problematicFrequencies.has(
        getFrequency(audit, 'matchingRolesFrequency') as FrequencyValue,
      ),
      row: {
        id: 'role-matching',
        finding:
          'Matching candidates to role expectations was a recurring challenge.',
        implementedImprovement: documentedFields(
          position,
          [
            ['keyOwnershipAreas', 'Ownership areas'],
            ['requiredSkills', 'required skills'],
            ['first3Months', 'first-three-month priorities'],
            ['success12Months', '12-month outcomes'],
          ],
          'Role requirements and expectations are documented in one shared intake.',
        ),
        nextStep: 'job-description',
        nextStepLabel: 'Refine Job Description',
      },
    },
    {
      isFinding: problematicFrequencies.has(
        getFrequency(audit, 'candidateExperienceFrequency') as FrequencyValue,
      ),
      row: {
        id: 'candidate-experience',
        finding: 'Candidate experience was a recurring challenge.',
        implementedImprovement: facts.stageCount
          ? `Candidate-facing role expectations and this ${facts.stageCount}-stage process are ready to share in the Candidate Packet.`
          : 'Candidate-facing role expectations and preparation guidance are ready to share in the Candidate Packet.',
        nextStep: 'candidate-packet',
        nextStepLabel: 'Review Candidate Packet',
      },
    },
    {
      isFinding: problematicFrequencies.has(
        getFrequency(audit, 'internalChallengesFrequency') as FrequencyValue,
      ),
      row: {
        id: 'internal-alignment',
        finding: 'Internal alignment and decisions were a recurring challenge.',
        implementedImprovement: documentedFields(
          position,
          [
            ['mainCommunicationChannel', 'Communication channel'],
            ['feedbackAgreements', 'feedback agreements'],
            ['interviewCoordinator', 'interview coordination'],
            ['roleClosingMethod', 'decision method'],
          ],
          'Hiring expectations and decisions are documented in the shared intake.',
        ),
        nextStep: 'hiring-decision-matrix',
        nextStepLabel: 'Define Decision Rights',
      },
    },
    {
      isFinding: problematicFrequencies.has(
        getFrequency(audit, 'candidateSourcingFrequency') as FrequencyValue,
      ),
      row: {
        id: 'candidate-sourcing',
        finding:
          'Finding and engaging qualified candidates was a recurring challenge.',
        implementedImprovement: documentedFields(
          position,
          [
            ['requiredSkills', 'Required skills'],
            ['niceToHaveSkills', 'preferred skills'],
            ['companySellingPoint', 'role selling points'],
          ],
          'The intake provides role criteria for more targeted sourcing.',
        ),
        nextStep: 'boolean-search',
        nextStepLabel: 'Build Boolean Search',
      },
    },
    {
      isFinding: problematicFrequencies.has(
        getFrequency(audit, 'postHireChallengesFrequency') as FrequencyValue,
      ),
      row: {
        id: 'post-hire',
        finding: 'Post-hire outcomes were a recurring challenge.',
        implementedImprovement: documentedFields(
          position,
          [
            ['first3Months', 'First-three-month priorities'],
            ['success12Months', '12-month success outcomes'],
          ],
          'Success expectations are documented before interviewing begins.',
        ),
        nextStep: 'interview-preparation',
        nextStepLabel: 'Align Interview Questions',
      },
    },
  ]

  return candidates
    .filter(({ isFinding }) => isFinding)
    .slice(0, limit)
    .map(({ row }) => row)
}

export function formatAuditUpdatedDate(updatedAt: string): string {
  const date = new Date(updatedAt)
  if (Number.isNaN(date.getTime())) return 'Date unavailable'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}
